import {PaymentMethodResult} from '@stripe/stripe-js/dist/stripe-js/stripe';
import {OnApproveData} from '@paypal/paypal-js/types/components/buttons';
import {api} from '~/common/checkout/api/api';

interface PackageResponse {
  packages: Package[];
}

interface BaseCartQueryArgs {
  apiVersion: string;
  eventId?: string;
  checkoutType?: CheckoutType;
}

type CartQueryArgs = BaseCartQueryArgs & {
  transactionId?: string | null;
};

interface UpdateCartArgs {
  data: Partial<Cart>;
}

type CartUpdateQueryArgs = CartQueryArgs & UpdateCartArgs;

export interface SetDiscountData {
  code: string;
}

interface SetDiscountArgs {
  data: SetDiscountData;
}

type CartDiscountQueryArgs = CartQueryArgs & SetDiscountArgs;

export interface CheckoutData {
  paymentType: string;
  paymentData: PaymentMethodResult | OnApproveData | Record<string, never>;
  packageId?: string;
  useSavedCard: boolean;
  total: number;
}

interface Checkout {
  data: CheckoutData;
}

type CheckoutArgs = CartQueryArgs & Checkout;

export interface CheckoutResponse {
  quantity: number;
  total: number;
  tax: number;
  subtotal: number;
  preDiscountSubtotal?: number;
  invoiceLineItem: string | null;
  paymentMethodDescription: string | null;
  guestsAdded: number;
  paymentMethod: string;
  package: string;
}

const cartApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPackages: builder.query<Package[], BaseCartQueryArgs>({
      providesTags: ['Packages'],
      query: ({apiVersion, eventId}) => ({
        url: `/${apiVersion}/event/packages/${eventId}`,
        method: 'GET',
      }),
      transformResponse(response: PackageResponse) {
        const data = response.packages;
        return data as Package[];
      },
    }),
    getCart: builder.query<Cart, BaseCartQueryArgs>({
      providesTags: ['Cart'],
      query: ({apiVersion, eventId, checkoutType}) => ({
        url: `/${apiVersion}/${checkoutType}/${eventId}`,
      }),
    }),
    updateCart: builder.mutation<Cart, CartUpdateQueryArgs>({
      query: ({apiVersion, eventId, checkoutType, transactionId, data}) => ({
        url: `/${apiVersion}/${checkoutType}/${eventId}`,
        method: 'POST',
        body: {
          ...data,
          transactionId,
        },
      }),
      async onQueryStarted({apiVersion, eventId, checkoutType}, {dispatch, queryFulfilled}) {
        try {
          const result = await queryFulfilled;
          const data = result.data as Cart;
          dispatch(
            cartApi.util.updateQueryData(
              'getCart',
              {apiVersion, eventId, checkoutType},
              (draft) => ({...draft, ...data})
            )
          );
        } catch {
          // ignored
        }
      },
    }),
    setDiscount: builder.mutation<Cart, CartDiscountQueryArgs>({
      query: ({apiVersion, eventId, checkoutType, transactionId, data}) => ({
        url: `/${apiVersion}/${checkoutType}/discounts/${eventId}`,
        method: 'POST',
        body: {
          ...data,
          transactionId,
        },
      }),
      async onQueryStarted({apiVersion, eventId, checkoutType, data}, {dispatch, queryFulfilled}) {
        // use optimistic updates to immediately set the coupon code
        const patchResult = dispatch(
          cartApi.util.updateQueryData('getCart', {apiVersion, eventId, checkoutType}, (draft) => ({
            ...draft,
            ...data,
          }))
        );
        try {
          const {data: newCart} = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData(
              'getCart',
              {apiVersion, eventId, checkoutType},
              () => newCart
            )
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteDiscount: builder.mutation<Cart, CartQueryArgs>({
      query: ({apiVersion, eventId, checkoutType, transactionId}) => ({
        url: `/${apiVersion}/${checkoutType}/discounts/${eventId}`,
        method: 'DELETE',
        body: {
          transactionId,
          code: undefined,
        },
      }),
      async onQueryStarted({apiVersion, eventId, checkoutType}, {dispatch, queryFulfilled}) {
        // use optimistic updates to immediately remove the coupon code
        const patchResult = dispatch(
          cartApi.util.updateQueryData('getCart', {apiVersion, eventId, checkoutType}, (draft) => ({
            ...draft,
            code: undefined,
          }))
        );
        try {
          const {data: newCart} = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData(
              'getCart',
              {apiVersion, eventId, checkoutType},
              () => newCart
            )
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    checkout: builder.mutation<CheckoutResponse, CheckoutArgs>({
      query: ({eventId, apiVersion, checkoutType, transactionId, data: body}) => {
        if (checkoutType === 'photo_share') {
          return {
            url: `/${apiVersion}/${checkoutType}/${eventId}/checkout`,
            method: 'POST',
            body: {
              ...body,
              transactionId,
            },
          };
        }
        return {
          url: `/${apiVersion}/${checkoutType}/checkout/${eventId}`,
          method: 'POST',
          body: {
            ...body,
            transactionId,
          },
        };
      },
    }),
  }),
});

export const {
  useGetCartQuery,
  useLazyGetCartQuery,
  useGetPackagesQuery,
  useLazyGetPackagesQuery,
  useSetDiscountMutation,
  useDeleteDiscountMutation,
  useUpdateCartMutation,
  useCheckoutMutation,
} = cartApi;
